// The route sheet map's stops: what each marker, label, card and list row
// says, where Google placed it, which labels fit on screen, and where the
// map moves to show a stop. StopsMap draws them; RouteSheetMap puts the map
// together.

import {
  deliveryAddress,
  FULFILLED_STATUS,
  lineStates,
  orderPieces,
  productName,
} from "./routeSheet";

const clean = (value) => String(value ?? "").trim();

// The Maps JavaScript API as the app loads it (the script in
// public/index.html, the one Directions already uses), when it has what the
// map draws with; null when it didn't load.
export const mapsApi = () => {
  const maps = window.google?.maps;
  return maps?.Map &&
    maps.OverlayView &&
    maps.Polyline &&
    maps.LatLngBounds &&
    maps.DirectionsService
    ? maps
    : null;
};

// The start location's key on the map and in the list (a stop's is its
// order's id).
export const START = "start";

export const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, "")}`;

// What's still to deliver to an order: each line not delivered yet and how
// many ([{ id, name, quantity }]); [] once it's fulfilled.
export const stillToDeliver = (order) =>
  lineStates(order)
    .filter((line) => line.state !== "fulfilled" && line.state !== "cancelled")
    .map((line) => ({
      id: line.item?.id,
      name: productName(line.item) || "Item",
      quantity: line.state === "attention" ? line.pending : line.quantity,
    }))
    .filter((line) => line.quantity > 0);

const DELIVERY_NOTES = [
  [/^Delivered ·/, "delivered"],
  [/^Partly delivered ·/, "partly"],
  [/^Not delivered \(/, "not_delivered"],
];
const IMAGE = /\.(jpe?g|png|gif|webp|bmp)(\?|#|$)/i;
const NOTE_DETAILS = /^(Items|Delivered|Still to deliver):/;

/**
 * An order's deliveries as the delivery list noted them (each delivery,
 * part delivery or try, with the driver's photos), newest first: [{ id,
 * when, kind, details, photos }]. `kind` is delivered, partly or
 * not_delivered; `details` the note's Items / Delivered / Still to deliver
 * lines; `photos` the pictures saved with it.
 */
export const deliveryHistory = (order) =>
  (Array.isArray(order?.order_notes) ? order.order_notes : [])
    .map((note) => {
      const [heading = "", ...rest] = clean(note?.note).split("\n");
      const kind = DELIVERY_NOTES.find(([test]) => test.test(heading))?.[1];
      if (!kind) return null;
      return {
        id: note?.id,
        when: note?.created_at ?? null,
        kind,
        details: rest.map(clean).filter((line) => NOTE_DETAILS.test(line)),
        photos: (note?.order_attachments ?? [])
          .map((file) => clean(file?.file))
          .filter((url) => IMAGE.test(url)),
      };
    })
    .filter(Boolean)
    .sort((a, b) => String(b.when ?? "").localeCompare(String(a.when ?? "")));

/**
 * The sheet's orders as the map's stops, numbered as the sheet numbers them
 * (an order without a delivery address keeps its number but isn't on the
 * map): [{ id, number, reference, customer, phone, address, pieces,
 * toDeliver, history, fulfilled }] (toDeliver: stillToDeliver; history:
 * deliveryHistory).
 * `reference` is the marketplace's order number (order_id, e.g. Amazon's
 * "701-4158496-8146622"), the one staff know an order by; `id` is Bolt's.
 */
export const mapStops = (orders) =>
  orders.map((order, index) => {
    const shipping = order?.order_shipping_address_details;
    return {
      id: order?.id,
      number: index + 1,
      reference: clean(order?.order_id),
      customer: [shipping?.first_name, shipping?.last_name]
        .map(clean)
        .filter(Boolean)
        .join(" "),
      phone: clean(shipping?.phone),
      address: deliveryAddress(shipping),
      pieces: orderPieces(order),
      toDeliver: stillToDeliver(order),
      history: deliveryHistory(order),
      fulfilled: Number(order?.order_status_id) === FULFILLED_STATUS,
    };
  });

// A stop's name on its marker, label and card: the reference, else Bolt's
// number.
export const stopName = (stop) => stop.reference || `#${stop.id}`;

// The line under it: Bolt's number (when the reference is the name) and
// the pieces.
export const stopDetails = (stop) =>
  [stop.reference && `#${stop.id}`, `${stop.pieces} pcs`]
    .filter(Boolean)
    .join(" · ");

/**
 * Where Google placed each address of a drive through `points`, from the
 * drive's legs (leg k runs from point k to point k + 1): a Map of address
 * → { lat, lng }.
 */
export const placesOf = (points, legs = []) => {
  const places = new Map();
  legs.forEach((leg, k) => {
    if (leg?.from && points[k] != null) places.set(points[k], leg.from);
    if (leg?.to && points[k + 1] != null) places.set(points[k + 1], leg.to);
  });
  return places;
};

/**
 * The map centre that puts `place` `pixels` below the middle of the map at
 * `zoom` (Web Mercator, 256 px tiles), so the card above its marker fits.
 */
export const centreAbove = (place, pixels, zoom) => {
  const size = 256 * 2 ** zoom;
  const sin = Math.sin((place.lat * Math.PI) / 180);
  const y =
    (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size - pixels;
  const n = Math.PI - (2 * Math.PI * y) / size;
  return { lat: (Math.atan(Math.sinh(n)) * 180) / Math.PI, lng: place.lng };
};

// Labels beside the markers, in pixels: the gap to the marker, the widest
// one, and roughly how wide a character of their bold 11 px line is.
const LABEL = { gap: 5, max: 184, perChar: 6.6, padding: 18 };

// A label's width from its lines.
export const labelWidth = (lines) =>
  Math.min(
    LABEL.max,
    Math.round(
      Math.max(0, ...lines.map((line) => String(line).length)) * LABEL.perChar,
    ) + LABEL.padding,
  );

const overlaps = (a, b) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/**
 * Which markers get a label: right of the marker, else left, where it
 * covers no marker and no label placed before it, and stays inside `view`
 * (the map's { left, top, right, bottom }, in the markers' pixels; null when
 * it isn't known). `marks` ([{ key, box, width, height }], box: the
 * marker's { left, right, top, bottom }) are labelled in the order given; a
 * mark without a width gets none. Returns a Map of key → "right" | "left".
 */
export const placeLabels = (marks, view) => {
  const taken = marks.map((mark) => mark.box);
  const sides = new Map();
  marks.forEach((mark) => {
    if (!mark.width) return;
    const top = mark.box.top;
    const bottom = top + mark.height;
    const tries = [
      ["right", mark.box.right + LABEL.gap],
      ["left", mark.box.left - LABEL.gap - mark.width],
    ];
    const found = tries.find(([, left]) => {
      const box = { left, right: left + mark.width, top, bottom };
      const inside =
        !view ||
        (box.left >= view.left &&
          box.right <= view.right &&
          box.top >= view.top &&
          box.bottom <= view.bottom);
      if (!inside || taken.some((other) => overlaps(box, other))) {
        return false;
      }
      taken.push(box);
      return true;
    });
    if (found) sides.set(mark.key, found[0]);
  });
  return sides;
};
